import { useState, useEffect } from 'react'
import { useStory } from '../contexts/StoryContext'
import Analytics from '../services/analytics'
import './Onboarding.css'

function Onboarding({ onComplete }) {
  const { setUserSettings, userSettings } = useStory()
  const [step, setStep] = useState(0)
  const [detectedLocale, setDetectedLocale] = useState(null)
  const [selectedLanguage, setSelectedLanguage] = useState('en')
  const [config, setConfig] = useState({
    culture: null,
    ageGroup: null,
    learnerMode: false,
    nativeLanguage: null,
    targetLanguage: null
  })

  // Track app launch
  useEffect(() => {
    Analytics.appLaunch()
  }, [])

  // Detect device language and region on mount
  useEffect(() => {
    const lang = navigator.language || 'en-US'
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    // Simple region detection from timezone
    let region = 'US'
    if (timezone.includes('Mexico')) region = 'MX'
    else if (timezone.includes('Sao_Paulo') || timezone.includes('Brazil')) region = 'BR'
    else if (timezone.includes('Madrid') || timezone.includes('Europe/Madrid')) region = 'ES'
    else if (timezone.includes('Tokyo')) region = 'JP'
    
    const langCode = lang.split('-')[0]
    setDetectedLocale({ language: langCode, region, full: `${langCode}-${region}` })
    
    // Set initial language based on device
    if (langCode === 'es') {
      setSelectedLanguage('es')
    }
    
    // Check for mismatch (e.g., Spanish device in US)
    const deviceLang = langCode
    const isMismatch = (deviceLang === 'es' && region === 'US') ||
                       (deviceLang === 'en' && ['MX', 'ES', 'BR'].includes(region))
    
    if (isMismatch) {
      setStep(1) // Go to mismatch question
    }
  }, [])

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang)
    Analytics.languageSet(lang)
  }

  const handleComplete = () => {
    const finalSettings = {
      ...config,
      culture: selectedLanguage === 'es' ? 'es-MX' : 'en-US',
      language: selectedLanguage
    }
    
    setUserSettings(finalSettings)
    Analytics.onboardingComplete(finalSettings)
    onComplete()
  }

  const goBack = () => {
    if (step === 1) {
      setStep(0)
    } else if (step === 2) {
      const lang = navigator.language || 'en-US'
      const langCode = lang.split('-')[0]
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      let region = 'US'
      if (timezone.includes('Mexico')) region = 'MX'
      else if (timezone.includes('Sao_Paulo') || timezone.includes('Brazil')) region = 'BR'
      
      const isMismatch = (langCode === 'es' && region === 'US') ||
                         (langCode === 'en' && ['MX', 'ES', 'BR'].includes(region))
      
      setStep(isMismatch ? 1 : 0)
    } else if (step === 3) {
      setStep(2)
    }
  }

  // Text content based on selected language
  const text = {
    en: {
      title: '📚 StoryPath',
      tagline: 'Adventures that feel like home',
      letsGo: "Let's Go! →",
      languageLabel: 'Language',
      mismatchTitle: '🌍 A quick question!',
      mismatchQuestion: 'Is English your home language, or are you learning it?',
      homeLanguage: '🏠 Home Language',
      learningIt: '📖 Learning It',
      howOld: 'How old are you?',
      imLittle: "I'm little",
      imMiddle: "I'm in the middle",
      imBigger: "I'm bigger",
      whatToDo: 'What would you like to do?',
      readAdventures: 'Read Adventures',
      exploreStories: 'Explore curated stories',
      createAdventures: 'Create Adventures',
      makeOwn: 'Make your own stories with AI',
      back: '← Back'
    },
    es: {
      title: '📚 StoryPath',
      tagline: 'Aventuras que se sienten como en casa',
      letsGo: '¡Vamos! →',
      languageLabel: 'Idioma',
      mismatchTitle: '🌍 ¡Una pregunta rápida!',
      mismatchQuestion: '¿El español es tu idioma nativo o lo estás aprendiendo?',
      homeLanguage: '🏠 Idioma Nativo',
      learningIt: '📖 Lo Estoy Aprendiendo',
      howOld: '¿Cuántos años tienes?',
      imLittle: 'Soy pequeño',
      imMiddle: 'Estoy en el medio',
      imBigger: 'Soy más grande',
      whatToDo: '¿Qué te gustaría hacer?',
      readAdventures: 'Leer Aventuras',
      exploreStories: 'Explorar historias seleccionadas',
      createAdventures: 'Crear Aventuras',
      makeOwn: 'Crea tus propias historias con IA',
      back: '← Atrás'
    }
  }
  
  const t = text[selectedLanguage]

  const renderStep = () => {
    switch (step) {
      case 0: // Welcome
        return (
          <div className="onboarding-step welcome">
            <h1>{t.title}</h1>
            <p className="tagline">{t.tagline}</p>
            
            {/* Language Selector */}
            <div className="language-selector">
              <span className="language-label">{t.languageLabel}:</span>
              <div className="language-buttons">
                <button 
                  className={`lang-btn ${selectedLanguage === 'en' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('en')}
                >
                  🇺🇸 English
                </button>
                <button 
                  className={`lang-btn ${selectedLanguage === 'es' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('es')}
                >
                  🇲🇽 Español
                </button>
              </div>
            </div>
            
            <button className="big-btn" onClick={() => setStep(2)}>
              {t.letsGo}
            </button>
          </div>
        )

      case 1: // Language mismatch
        return (
          <div className="onboarding-step mismatch">
            <button className="back-btn-onboarding" onClick={goBack}>
              {t.back}
            </button>
            <h2>{t.mismatchTitle}</h2>
            <p>{t.mismatchQuestion}</p>
            <div className="choice-buttons">
              <button 
                className="choice-btn"
                onClick={() => {
                  setConfig({ ...config, learnerMode: false })
                  setStep(2)
                }}
              >
                {t.homeLanguage}
              </button>
              <button 
                className="choice-btn"
                onClick={() => {
                  setConfig({ 
                    ...config, 
                    learnerMode: true,
                    nativeLanguage: detectedLocale?.language === 'es' ? 'en' : 'es',
                    targetLanguage: detectedLocale?.language
                  })
                  setStep(2)
                }}
              >
                {t.learningIt}
              </button>
            </div>
          </div>
        )

      case 2: // Age selection
        return (
          <div className="onboarding-step age">
            <button className="back-btn-onboarding" onClick={goBack}>
              {t.back}
            </button>
            <h2>{t.howOld}</h2>
            <div className="age-buttons">
              <button 
                className="age-btn"
                onClick={() => {
                  setConfig({ ...config, ageGroup: 'little' })
                  setStep(3)
                }}
              >
                <span className="age-icon">🧒</span>
                <span>{t.imLittle}</span>
                <span className="age-range">(4-6)</span>
              </button>
              <button 
                className="age-btn"
                onClick={() => {
                  setConfig({ ...config, ageGroup: 'middle' })
                  setStep(3)
                }}
              >
                <span className="age-icon">🧑</span>
                <span>{t.imMiddle}</span>
                <span className="age-range">(7-9)</span>
              </button>
              <button 
                className="age-btn"
                onClick={() => {
                  setConfig({ ...config, ageGroup: 'bigger' })
                  setStep(3)
                }}
              >
                <span className="age-icon">🧑‍🎓</span>
                <span>{t.imBigger}</span>
                <span className="age-range">(10-12)</span>
              </button>
            </div>
          </div>
        )

      case 3: // Mode selection (only for ages 7+)
        if (config.ageGroup === 'little') {
          handleComplete()
          return null
        }
        return (
          <div className="onboarding-step mode">
            <button className="back-btn-onboarding" onClick={goBack}>
              {t.back}
            </button>
            <h2>{t.whatToDo}</h2>
            <div className="mode-buttons">
              <button 
                className="mode-btn"
                onClick={handleComplete}
              >
                <span className="mode-icon">📖</span>
                <span className="mode-title">{t.readAdventures}</span>
                <span className="mode-desc">{t.exploreStories}</span>
              </button>
              <button 
                className="mode-btn"
                onClick={handleComplete}
              >
                <span className="mode-icon">✨</span>
                <span className="mode-title">{t.createAdventures}</span>
                <span className="mode-desc">{t.makeOwn}</span>
              </button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="onboarding">
      {renderStep()}
    </div>
  )
}

export default Onboarding
